<template>
  <transition name="pwa-banner-slide">
    <div v-if="isVisible" class="pwa-install-banner" :class="{ behind: stackIndex > 0 }">
      <div class="banner-text">
        <h4 class="banner-title">{{ $t("DownloadAppPage.bannerTitle") }}</h4>
        <p class="banner-subtitle">{{ $t("DownloadAppPage.bannerSubTitle") }}</p>
      </div>
      <div class="banner-actions">
        <v-btn
          class="banner-install-btn"
          color="#bd9478"
          size="small"
          height="34"
          rounded="lg"
          :loading="loading"
          @click="install"
        >
          <span>{{ $t("DownloadAppPage.bannerInstall") }}</span>
        </v-btn>
        <v-btn
          class="banner-later-btn"
          variant="text"
          size="small"
          height="28"
          @click="dismiss"
        >
          <span>{{ $t("DownloadAppPage.bannerLater") }}</span>
        </v-btn>
      </div>

      <!-- Manual install instructions (iOS / browsers without a native prompt) -->
      <v-bottom-sheet v-model="bottomSheet">
        <v-card
          class="text-center rounded-xl ma-auto mb-5"
          height="270"
          max-width="95%"
        >
          <v-icon
            class="sheet-icon"
            color="#444444"
            icon="mdi-close"
            @click="bottomSheet = false"
          />
          <v-card-text class="mt-3">
            <div
              class="bottomSheet-content d-flex flex-column justify-start ga-2"
            >
              <h1>
                {{
                  isIOS
                    ? $t("DownloadAppPage.titleIOSSheet")
                    : isAndroid
                    ? $t("DownloadAppPage.titleAndroidSheet")
                    : $t("DownloadAppPage.titleDesktopSheet")
                }}
              </h1>
              <p>
                {{
                  isIOS
                    ? $t("DownloadAppPage.subTitleIOSSheet")
                    : isAndroid
                    ? $t("DownloadAppPage.subTitleAndroidSheet")
                    : $t("DownloadAppPage.subTitleDesktopSheet")
                }}
              </p>
              <ol class="text-right pr-4">
                <li>
                  {{
                    isIOS
                      ? $t("DownloadAppPage.sheetInstructIOSOne")
                      : isAndroid
                      ? $t("DownloadAppPage.sheetInstructAndroidOne")
                      : $t("DownloadAppPage.sheetInstructDesktopOne")
                  }}
                  <v-icon
                    v-if="isIOS || isAndroid"
                    class="instructIcon one"
                    :icon="isIOS ? 'mdi-export-variant' : 'mdi-dots-vertical'"
                  />
                  <img
                    v-else
                    alt="desktop-icon"
                    src="@/assets/images/desktop-icon.png"
                  />
                </li>
                <li>
                  {{
                    isIOS || isAndroid
                      ? $t("DownloadAppPage.sheetInstructTwo")
                      : $t("DownloadAppPage.sheetInstructDesktopTwo")
                  }}
                  <span v-if="isAndroid || isIOS" class="instructIcon two"
                    >{{ $t("DownloadAppPage.sheetInstructTwoDes") }}
                    <v-icon
                      :icon="
                        isIOS
                          ? 'mdi-plus-box-outline'
                          : 'mdi-cellphone-arrow-down-variant'
                      "
                  /></span>
                  <div v-else>
                    <img
                      class="desktop-pwa-install-picture"
                      alt="desktop-pwa-install-picture"
                      src="@/assets/images/desktop-pwa-install.png"
                    />
                  </div>
                </li>
              </ol>
            </div>
          </v-card-text>
        </v-card>
      </v-bottom-sheet>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from "vuex";
import {
  registerBanner,
  unregisterBanner,
  bannerIndex,
} from "../../utils/bannerStack";

const BANNER_NAME = "pwa-install";
const DISMISS_KEY = "pwaInstallBannerDismissedAt";
// Re-show the banner this long after the user dismisses it (7 days).
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;

export default {
  data() {
    return {
      deferredPrompt: window.deferredPwaPrompt || null,
      bottomSheet: false,
      loading: false,
      dismissed: this.isRecentlyDismissed(),
      installed: false,
    };
  },
  computed: {
    ...mapGetters({
      isPwaInstalled: "isPwaInstalled",
    }),
    stackIndex() {
      return bannerIndex(BANNER_NAME);
    },
    isVisible() {
      return !this.isPwaInstalled && !this.installed && !this.dismissed;
    },
    isIOS() {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return (
        /iphone|ipad|ipod/.test(userAgent) ||
        // iPadOS reports as desktop Safari
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      );
    },
    isAndroid() {
      return /android/.test(window.navigator.userAgent.toLowerCase());
    },
  },
  mounted() {
    window.addEventListener("pwa-installable", this.onInstallable);
    window.addEventListener("pwa-installed", this.onInstalled);
    if (this.isVisible) {
      registerBanner(BANNER_NAME, 2); // behind the push opt-in banner
    }
  },
  unmounted() {
    window.removeEventListener("pwa-installable", this.onInstallable);
    window.removeEventListener("pwa-installed", this.onInstalled);
    unregisterBanner(BANNER_NAME);
  },
  methods: {
    isRecentlyDismissed() {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY));
      if (!dismissedAt) return false;
      return Date.now() - dismissedAt < DISMISS_DURATION;
    },
    onInstallable() {
      this.deferredPrompt = window.deferredPwaPrompt;
    },
    onInstalled() {
      this.installed = true;
      unregisterBanner(BANNER_NAME);
    },
    async install() {
      // Native install prompt (Android Chrome, desktop Chrome/Edge)
      if (this.deferredPrompt) {
        this.loading = true;
        try {
          this.deferredPrompt.prompt();
          const { outcome } = await this.deferredPrompt.userChoice;
          if (outcome === "accepted") {
            this.installed = true;
          }
          this.deferredPrompt = null;
          window.deferredPwaPrompt = null;
        } catch (err) {
          console.log(err);
        } finally {
          this.loading = false;
        }
        return;
      }
      // Fallback: manual "Add to Home Screen" instructions (iOS, etc.)
      this.bottomSheet = true;
    },
    dismiss() {
      this.dismissed = true;
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
      unregisterBanner(BANNER_NAME);
    },
  },
};
</script>

<style lang="scss">
.v-overlay__content.v-bottom-sheet__content {
  box-shadow: unset !important;
}
</style>

<style scoped lang="scss">
.pwa-install-banner {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 11;
  display: flex;
  align-items: center;
  gap: 14px;
  width: calc(100% - 32px);
  max-width: 520px;
  padding: 14px 16px;
  background: #ffffffee;
  border: 1px solid #ececec;
  border-radius: 16px;
  box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.15);
  transition: transform 0.35s ease, opacity 0.35s ease;

  // Card-deck look: the non-front banner peeks out behind the front one
  &.behind {
    transform: translateX(-50%) translateY(-14px) scale(0.96);
    z-index: 10;
    pointer-events: none;
  }

  .banner-text {
    flex: 1;
    min-width: 0;

    .banner-title {
      font-size: 18px;
      font-weight: 600;
      color: $blackColor;
      margin: 0;
    }

    .banner-subtitle {
      font-size: 14px;
      color: #7c7c7c;
      margin: 2px 0 0 0;
    }
  }

  .banner-actions {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
  }

  .banner-install-btn {
    color: #ffffff !important;
    font-size: 14px;
  }

  .banner-later-btn {
    color: #9e9e9e !important;
    font-size: 13px;
    min-width: unset;
  }
}

// Slide-up enter / leave transition
.pwa-banner-slide-enter-active,
.pwa-banner-slide-leave-active {
  transition: transform 0.35s ease, opacity 0.35s ease;
}
.pwa-banner-slide-enter-from,
.pwa-banner-slide-leave-to {
  transform: translate(-50%, 120%);
  opacity: 0;
}

.desktop-pwa-install-picture {
  width: 400px;
}

.bottomSheet-content {
  h1 {
    font-size: $h1TextTitleSize;
    text-align: right;
    color: $blackColor;
    margin: 0;
  }
  p {
    text-align: right;
    width: 100%;
    color: #7c7c7c;
  }
  @media (max-width: 601px) {
    h1 {
      font-size: 30px;
    }
  }
  ol {
    font-size: $mediumTextSize;
    color: $blackColor;
    li {
      margin-bottom: 10px;
    }
  }
}

.instructIcon {
  background: $iconBackgroundColor;
  color: $blackColor;
  margin-right: 10px;
}
.instructIcon.one {
  font-size: 25px;
}
.instructIcon.two {
  font-size: 14px;
  padding: 3px 5px;

  .v-icon {
    margin-right: 10px;
  }
}

.sheet-icon {
  position: absolute;
  top: 15px;
  left: 15px;
}

@media (max-width: 601px) {
  .pwa-install-banner {
    gap: 10px;
    padding: 10px 12px;

    .banner-text .banner-title {
      font-size: 16px;
    }
    .banner-text .banner-subtitle {
      font-size: 12px;
    }
  }
}
</style>
